import { getOfferCapabilities } from "../../../common/offerCapabilities";
import OtaEmailNotice, { useEmailLinkAcknowledgement } from "./OtaEmailNotice";
import React, { useState, useMemo } from "react";
import {
  Box,
  CheckBox,
  Heading,
  Tip,
  FormField,
  Select,
  Button,
  Layer,
  TextArea,
  Text,
} from "grommet";
import { CircleInformation } from "grommet-icons";
import { PriceInput } from "@shared";
import { CURRENCY_OPTIONS_LIST } from "@common/utils";
import { PMS_RESERVIT, PMS_WEBREZPRO } from "@common/constants";
import Markdown from "markdown-to-jsx";
import { CustomTabs } from "@features/shared/CustomTabs";
import LocalizedEmailEditor from "./LocalizedEmailEditor";
import LocalizedTextEditor, { SMS_MAX_LENGTH } from "./LocalizedTextEditor";
import { useTranslation } from "react-i18next";

// Returns true if `text` contains the given tag, either as a {tagName} literal
// or as a tag-pill span (data-tag="tagName") emitted by the rich-text editor.
const containsTag = (text, tag) => {
  if (!text) return false;
  return text.includes(`{${tag}}`) || text.includes(`data-tag="${tag}"`);
};

const NUMBER_EMOJIS = ["1️⃣", "2️⃣", "3️⃣", "4️⃣", "5️⃣", "6️⃣", "7️⃣", "8️⃣", "9️⃣"];

function StepItem({
  number,
  label,
  enabled,
  fixed,
  disabled,
  onToggle,
  tipContent,
  children,
}) {
  const isBlocked = fixed || disabled;
  let icon;
  if (disabled) icon = "🚫";
  else if (enabled) icon = NUMBER_EMOJIS[number - 1];
  else icon = "⬜️";

  return (
    <Box>
      <Box
        className="checkin-step-row"
        direction="row"
        gap="small"
        align="center"
        onClick={isBlocked ? undefined : onToggle}
        style={{
          cursor: isBlocked ? "not-allowed" : "pointer",
          userSelect: "none",
        }}
        pad={{ vertical: "xxsmall" }}
      >
        <Text size="medium">{icon}</Text>
        <Text>{label}</Text>
        {tipContent && (
          <Tip content={tipContent}>
            <CircleInformation size="small" />
          </Tip>
        )}
        {children}
      </Box>
    </Box>
  );
}


export default function CheckinFlowPreCheckin({
  formValues,
  onChange,
  communications,
  onCommunicationsChange,
  onSaveCommunications,
  savedCommunications,
  hotel,
  defaults,
  automationManaged = false,
  channels,
}) {
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showRegistrationPreview, setShowRegistrationPreview] = useState(false);
  const [localRegistrationText, setLocalRegistrationText] = useState({
    en: "",
    fr: "",
  });

  const { t } = useTranslation();
  const linkAcknowledgement = useEmailLinkAcknowledgement(communications?.checkinEmail?.body);

  const pmsSlug = hotel?.pmsSlug || "";
  const isReservit = pmsSlug === PMS_RESERVIT;
  const isWebRezPro = pmsSlug === PMS_WEBREZPRO;
  const hasStripe = hotel?.paymentProcessorSlug === "stripe";

  const handleToggle = (name) => (e) => {
    onChange({ ...formValues, [name]: e.target.checked });
  };

  const handleValueChange = (name, value) => {
    onChange({ ...formValues, [name]: value });
  };

  const renderToggle = (label, name, disabled, tipContent) => (
    <Box direction="row" gap="small" align="center">
      <CheckBox
        name={name}
        checked={!!formValues[name]}
        onChange={handleToggle(name)}
        label={label}
        disabled={disabled}
        toggle
      />
      {tipContent && (
        <Tip content={tipContent}>
          <CircleInformation size="small" />
        </Tip>
      )}
    </Box>
  );

  // Build ordered list of optional steps with their visibility
  const optionalSteps = useMemo(() => {
    const steps = [
      {
        key: "offerEarlyCheckin",
        label: t("checkinFlow.preCheckin.earlyCheckin"),
        visible: getOfferCapabilities(hotel).upsells,
        tip: "Offered during online check-in if the guest's arrival time is before the hotel's check-in time. Set the price in Room Types.",
      },
      {
        key: "requestPhotoID",
        label: t("checkinFlow.preCheckin.photoId"),
        visible: true,
      },
      {
        key: "requestCardImprint",
        label: t("checkinFlow.preCheckin.deposit"),
        visible: hasStripe || isReservit,
        tip: hasStripe
          ? "A pre-authorization will be placed on the guest's card."
          : "Please configure Stripe with your API Keys in PMS Integration menu. Contact support for help.",
        disabled: !hasStripe,
      },
      {
        key: "registrationEnabled",
        label: t("checkinFlow.preCheckin.registrationCard"),
        visible: true,
      },
      {
        key: "webrezproAgreement",
        label: t("checkinFlow.preCheckin.wrpAgreement"),
        visible: isWebRezPro,
      },
    ];
    return steps.filter((s) => s.visible);
  }, [isReservit, isWebRezPro, hasStripe, t, hotel]);

  // Compute step numbers: fixed steps 1 & 2, then sequential for enabled optional steps
  const stepNumbers = useMemo(() => {
    const nums = {};
    let next = 3;
    for (const step of optionalSteps) {
      if (formValues[step.key] && !step.disabled) {
        nums[step.key] = next++;
      }
    }
    return nums;
  }, [optionalSteps, formValues]);

  const isOnlineCheckinEnabled = automationManaged || !!formValues.onlineCheckinEnabled;

  return (
    <Box id="section-precheckin" gap="medium">
      <Heading level={3} margin="none">
        {t(automationManaged ? "automation.precheckinSettings" : "checkinFlow.preCheckin.title")}
      </Heading>
      <Text size="small" color={automationManaged ? "dark-2" : "dark-4"}>
        {t(automationManaged ? "automation.descriptions.precheckin" : "checkinFlow.preCheckin.description")}
      </Text>

      {!automationManaged && renderToggle(t("checkinFlow.preCheckin.toggle"), "onlineCheckinEnabled")}

      {isOnlineCheckinEnabled && (
        <Box gap={automationManaged ? undefined : "small"}
          pad={automationManaged ? undefined : { left: "medium" }}
          border={automationManaged ? undefined : { side: "left", color: "light-4", size: "2px" }}>
        <Box
          className={automationManaged ? "automation-editor-section automation-special-panel" : undefined}
          gap="small"
          style={automationManaged ? undefined : { display: "contents" }}
        >
          <Heading level={4} margin={{ bottom: "xsmall", top: "none" }}>
            {t("checkinFlow.preCheckin.stepsHeading")}
          </Heading>

          {/* Fixed steps */}
          <StepItem
            number={1}
            label={t("checkinFlow.preCheckin.confirmGuests")}
            enabled
            fixed
          />
          <StepItem
            number={2}
            label={t("checkinFlow.preCheckin.arrivalTime")}
            enabled
            fixed
          />

          {/* Optional steps */}
          {optionalSteps.map((step) => {
            const enabled = !!formValues[step.key] && !step.disabled;
            const num = stepNumbers[step.key];
            return (
              <React.Fragment key={step.key}>
                <StepItem
                  number={num}
                  label={step.label}
                  enabled={enabled}
                  disabled={!!step.disabled}
                  onToggle={() => {
                    if (!step.disabled) {
                      onChange({
                        ...formValues,
                        [step.key]: !formValues[step.key],
                      });
                    }
                  }}
                  tipContent={step.tip}
                >
                  {step.key === "registrationEnabled" &&
                    formValues.registrationEnabled && (
                      <Box
                        direction="row"
                        gap="small"
                        margin={{ left: "small" }}
                      >
                        <Button
                          label={t("checkinFlow.preCheckin.preview")}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowRegistrationPreview(true);
                          }}
                        />
                        <Button
                          label={t("checkinFlow.preCheckin.configure")}
                          size="small"
                          primary
                          onClick={(e) => {
                            e.stopPropagation();
                            setLocalRegistrationText({
                              en: formValues.registrationTextEn || "",
                              fr: formValues.registrationTextFr || "",
                            });
                            setShowRegistrationModal(true);
                          }}
                        />
                      </Box>
                    )}
                </StepItem>

                {step.key === "registrationEnabled" &&
                  formValues.registrationEnabled && (
                    <Box pad={{ left: "medium" }}>
                      {!formValues.registrationTextEn?.trim() && (
                        <Text size="small" color="status-critical">
                          {t("checkinFlow.preCheckin.registrationMissing")}
                        </Text>
                      )}
                      {hotel?.language === "fr" &&
                        !formValues.registrationTextFr?.trim() && (
                          <Text size="small" color="status-warning">
                            {t("checkinFlow.preCheckin.frRegistrationMissing")}
                          </Text>
                        )}
                    </Box>
                  )}

                {step.key === "requestPhotoID" && formValues.requestPhotoID && (
                  <Box pad={{ left: "medium" }} margin={{ vertical: "xsmall" }}>
                    <Box direction="row" gap="small" align="center">
                      <CheckBox
                        checked={!!formValues.aiPreCheckEnabled}
                        onChange={handleToggle("aiPreCheckEnabled")}
                        label={t("checkinFlow.preCheckin.aiPreCheck")}
                      />
                      <Tip content={t("checkinFlow.preCheckin.aiPreCheckTip")}>
                        <CircleInformation size="small" />
                      </Tip>
                    </Box>
                  </Box>
                )}

                {step.key === "requestCardImprint" &&
                  formValues.requestCardImprint &&
                  hasStripe && (
                    <Box
                      direction="row"
                      gap="medium"
                      pad={{ left: "medium", top: "small" }}
                      wrap
                    >
                      <FormField
                        label={t(
                          "checkinFlow.preCheckin.preAuthorizationDeposit",
                        )}
                      >
                        <PriceInput
                          name="deposit"
                          value={formValues.deposit || 0}
                          onChange={(e) =>
                            handleValueChange("deposit", e.target.value)
                          }
                        />
                      </FormField>
                      <FormField label={t("checkinFlow.preCheckin.currency")}>
                        <Select
                          size="medium"
                          labelKey="label"
                          valueKey={{ key: "value", reduce: true }}
                          value={formValues.currency || "usd"}
                          options={CURRENCY_OPTIONS_LIST}
                          onChange={({ value }) =>
                            handleValueChange("currency", value)
                          }
                        />
                      </FormField>
                    </Box>
                  )}
              </React.Fragment>
            );
          })}

          <Box
            margin={{ top: "small" }}
            pad={{ top: "small" }}
            border={{ side: "top", color: "light-4" }}
            gap="small"
          >
            {renderToggle(
              t("checkinFlow.preCheckin.allStepsRequired"),
              "checkinStepsRequired",
              false,
              "When enabled, guests must complete all steps above before their check-in can be approved.",
            )}
          </Box>

          {/* OTAPrecheckinPolicy */}
          <Box margin={{ top: "medium" }} gap="small" width={{ max: "medium" }}>
            <FormField label={t("checkinFlow.preCheckin.otaPrecheckinPolicy")}>
              <Select
                size="medium"
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                value={formValues.OTAPrecheckinPolicy || "all"}
                options={[
                  {
                    label: t("checkinFlow.preCheckin.otaPolicyAll"),
                    value: "all",
                  },
                  {
                    label: t("checkinFlow.preCheckin.otaPolicyOnly"),
                    value: "only",
                  },
                  {
                    label: t("checkinFlow.preCheckin.otaPolicyNone"),
                    value: "none",
                  },
                ]}
                onChange={({ value }) =>
                  handleValueChange("OTAPrecheckinPolicy", value)
                }
              />
            </FormField>
          </Box>

        </Box>
          {/* Email */}
          <Box className={automationManaged ? "automation-editor-section automation-special-panel" : undefined} style={automationManaged && !channels?.includes("email") ? { display: "none" } : undefined} margin={{ top: "medium" }} gap="small">
            <Heading level={4} margin="none">
              {t("checkinFlow.common.email")}
            </Heading>
            {hotel?.checkinInvitationDisabled ||
            hotel?.disableAllCommunications ? (
              <Text size="small" color="status-critical">
                {t("checkinFlow.common.commsDisabled")}
              </Text>
            ) : (
              <>
                <LocalizedEmailEditor
                  value={communications?.checkinEmail || defaults?.checkinEmail}
                  onChange={(val) =>
                    onCommunicationsChange({
                      ...communications,
                      checkinEmail: val,
                    })
                  }
                  defaultValue={defaults?.checkinEmail}
                  useCustom={!!communications?.useCustomCheckinEmail}
                  onUseCustomChange={(v) => {
                    const updated = {
                      ...communications,
                      useCustomCheckinEmail: v,
                    };
                    onSaveCommunications(updated);
                  }}
                  template="pre-checkin"
                  upsells={communications?.checkinEmailUpsells || []}
                  currency={hotel?.currency}
                  onUpsellsChange={(upsells, email) => {
                    const updated = {
                      ...communications,
                      checkinEmail: email,
                      checkinEmailUpsells: upsells,
                    };
                    onSaveCommunications(updated);
                  }}
                  tagHint={t("checkinFlow.common.hintTags")}
                  tags={[
                    "firstName",
                    "lastName",
                    "hotel",
                    "hotelPhone",
                    "checkinDate",
                    "checkinTime",
                    "checkoutDate",
                    "checkoutTime",
                    "precheckinButton",
                    "stayDatesBox",
                    "mobileAppGuide",
                    "upsells",
                  ]}
                />
                {!!communications?.useCustomCheckinEmail &&
                  (() => {
                    const emailEn =
                      communications?.checkinEmail?.body?.en ??
                      defaults?.checkinEmail?.body?.en ??
                      "";
                    const emailFr =
                      communications?.checkinEmail?.body?.fr ??
                      defaults?.checkinEmail?.body?.fr ??
                      "";
                    const enEmpty = !emailEn?.replace(/<[^>]*>/g, "")?.trim();
                    const frEmpty = !emailFr?.replace(/<[^>]*>/g, "")?.trim();
                    const enMissingButton =
                      !enEmpty && !containsTag(emailEn, "precheckinButton");
                    const frMissingButton =
                      hotel?.language === "fr" &&
                      !frEmpty &&
                      !containsTag(emailFr, "precheckinButton");
                    const hasError =
                      enEmpty ||
                      enMissingButton ||
                      (hotel?.language === "fr" &&
                        (frEmpty || frMissingButton));
                    return (
                      <>
                        <Box gap="xsmall">
                          {enEmpty && (
                            <Text size="small" color="status-critical">
                              {t("checkinFlow.common.emailMissing")}
                            </Text>
                          )}
                          {enMissingButton && (
                            <Text size="small" color="status-critical">
                              {t("checkinFlow.common.precheckinButtonRequired")}
                            </Text>
                          )}
                          {hotel?.language === "fr" && frEmpty && (
                            <Text size="small" color="status-warning">
                              {t("checkinFlow.common.frEmailMissing")}
                            </Text>
                          )}
                          {hotel?.language === "fr" && frMissingButton && (
                            <Text size="small" color="status-critical">
                              {t(
                                "checkinFlow.common.frPrecheckinButtonRequired",
                              )}
                            </Text>
                          )}
                        </Box>
                        {!automationManaged && <Box align="end" gap="xsmall">
                          <OtaEmailNotice hotel={hotel} acknowledgement={linkAcknowledgement} />
                          <Text size="xsmall" color={automationManaged ? "dark-2" : "dark-4"}>
                            {t("checkinFlow.common.pressEmailSave")}
                          </Text>
                          <Button
                            label={t("checkinFlow.common.save")}
                            primary
                            size="small"
                            disabled={linkAcknowledgement.blocked || 
                              hasError ||
                              JSON.stringify(communications?.checkinEmail) ===
                                JSON.stringify(
                                  savedCommunications?.checkinEmail,
                                )
                            }
                            onClick={() => onSaveCommunications()}
                          />
                        </Box>}
                      </>
                    );
                  })()}
              </>
            )}
          </Box>

          {/* SMS */}
          <Box className={automationManaged ? "automation-editor-section automation-special-panel" : undefined} style={automationManaged && !channels?.includes("sms") ? { display: "none" } : undefined} margin={{ top: "medium" }} gap="small">
            <Heading level={4} margin="none">
              {t("checkinFlow.common.textMessage")}
            </Heading>
            {hotel?.checkinInvitationDisabled ||
            hotel?.disableAllCommunications ? (
              <Text size="small" color="status-critical">
                {t("checkinFlow.common.commsDisabled")}
              </Text>
            ) : (
              <>
                <LocalizedTextEditor
                  value={communications?.checkinText || defaults?.checkinText}
                  onChange={(val) =>
                    onCommunicationsChange({
                      ...communications,
                      checkinText: val,
                    })
                  }
                  defaultValue={defaults?.checkinText}
                  useCustom={!!communications?.useCustomCheckinText}
                  onUseCustomChange={(v) => {
                    const updated = {
                      ...communications,
                      useCustomCheckinText: v,
                    };
                    onSaveCommunications(updated);
                  }}
                  hint={t("checkinFlow.common.hintTags")}
                  maxLength={SMS_MAX_LENGTH}
                  tags={[
                    "firstName",
                    "lastName",
                    "hotel",
                    "checkinDate",
                    "checkinTime",
                    "checkoutDate",
                    "checkoutTime",
                    "magicLink",
                  ]}
                />
                {!!communications?.useCustomCheckinText &&
                  (() => {
                    const textEn =
                      communications?.checkinText?.en ??
                      defaults?.checkinText?.en ??
                      "";
                    const textFr =
                      communications?.checkinText?.fr ??
                      defaults?.checkinText?.fr ??
                      "";
                    const enEmpty = !textEn?.trim();
                    const frEmpty = !textFr?.trim();
                    const enMissingLink =
                      !enEmpty && !containsTag(textEn, "magicLink");
                    const frMissingLink =
                      hotel?.language === "fr" &&
                      !frEmpty &&
                      !containsTag(textFr, "magicLink");
                    const tooLongEn = (textEn || "").length > SMS_MAX_LENGTH;
                    const tooLongFr = (textFr || "").length > SMS_MAX_LENGTH;
                    const hasError =
                      enEmpty ||
                      enMissingLink ||
                      tooLongEn ||
                      (hotel?.language === "fr" &&
                        (frEmpty || frMissingLink || tooLongFr));
                    return (
                      <>
                        <Box gap="xsmall">
                          {enEmpty && (
                            <Text size="small" color="status-critical">
                              {t("checkinFlow.common.textMissing")}
                            </Text>
                          )}
                          {enMissingLink && (
                            <Text size="small" color="status-critical">
                              {t("checkinFlow.common.magicLinkRequired")}
                            </Text>
                          )}
                          {hotel?.language === "fr" && frEmpty && (
                            <Text size="small" color="status-warning">
                              {t("checkinFlow.common.frTextMissing")}
                            </Text>
                          )}
                          {hotel?.language === "fr" && frMissingLink && (
                            <Text size="small" color="status-critical">
                              {t("checkinFlow.common.frMagicLinkRequired")}
                            </Text>
                          )}
                        </Box>
                        {!automationManaged && <Box align="end" gap="xsmall">
                          <Text size="xsmall" color={automationManaged ? "dark-2" : "dark-4"}>
                            {t("checkinFlow.common.pressTextSave")}
                          </Text>
                          <Button
                            label={t("checkinFlow.common.save")}
                            primary
                            size="small"
                            disabled={
                              hasError ||
                              JSON.stringify(communications?.checkinText) ===
                                JSON.stringify(savedCommunications?.checkinText)
                            }
                            onClick={() => onSaveCommunications()}
                          />
                        </Box>}
                      </>
                    );
                  })()}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Registration Card Configure Modal */}
      {showRegistrationModal && (
        <Layer
          onEsc={() => setShowRegistrationModal(false)}
          onClickOutside={() => setShowRegistrationModal(false)}
          responsive={false}
        >
          <Box
            pad="medium"
            gap="medium"
            width={{ max: "large", width: "90vw" }}
          >
            <Heading level={3} margin="none">
              {t("checkinFlow.preCheckin.registrationCardText")}
            </Heading>
            <Text size="small" color={automationManaged ? "dark-2" : "dark-4"}>
              {t("checkinFlow.preCheckin.formattingGuide")}
            </Text>
            <Text
              size="xsmall"
              color={automationManaged ? "dark-2" : "dark-4"}
              style={{ whiteSpace: "pre-line" }}
            >
              {t("checkinFlow.preCheckin.formattingGuideContent")}
            </Text>
            <CustomTabs
              tabs={[
                {
                  title: t("checkinFlow.preCheckin.english"),
                  key: "registrationTextEn",
                  content: (
                    <TextArea
                      value={localRegistrationText.en}
                      onChange={(e) =>
                        setLocalRegistrationText((prev) => ({
                          ...prev,
                          en: e.target.value,
                        }))
                      }
                      style={{ height: 400 }}
                    />
                  ),
                },
                {
                  title: t("checkinFlow.preCheckin.french"),
                  key: "registrationTextFr",
                  content: (
                    <TextArea
                      value={localRegistrationText.fr}
                      onChange={(e) =>
                        setLocalRegistrationText((prev) => ({
                          ...prev,
                          fr: e.target.value,
                        }))
                      }
                      style={{ height: 400 }}
                    />
                  ),
                },
              ]}
            />
            <Box direction="row" gap="small" justify="end">
              <Button
                label={t("checkinFlow.common.save")}
                onClick={() => {
                  onChange({
                    ...formValues,
                    registrationTextEn: localRegistrationText.en,
                    registrationTextFr: localRegistrationText.fr,
                  });
                  setShowRegistrationModal(false);
                }}
                primary
              />
            </Box>
          </Box>
        </Layer>
      )}

      {/* Registration Card Preview Modal */}
      {showRegistrationPreview && (
        <Layer
          onEsc={() => setShowRegistrationPreview(false)}
          onClickOutside={() => setShowRegistrationPreview(false)}
          responsive={false}
        >
          <Box
            pad="medium"
            gap="medium"
            width={{ max: "large", width: "90vw" }}
          >
            <Heading level={3} margin="none">
              {t("checkinFlow.preCheckin.registrationCardPreview")}
            </Heading>
            <CustomTabs
              tabs={[
                {
                  title: t("checkinFlow.preCheckin.english"),
                  key: "en",
                  content: (
                    <Box
                      background="white"
                      pad="medium"
                      round="small"
                      border={{ color: "light-4" }}
                      style={{ maxHeight: "70vh", overflow: "auto" }}
                    >
                      <Markdown>
                        {formValues.registrationTextEn || "_No content yet_"}
                      </Markdown>
                    </Box>
                  ),
                },
                {
                  title: t("checkinFlow.preCheckin.french"),
                  key: "fr",
                  content: (
                    <Box
                      background="white"
                      pad="medium"
                      round="small"
                      border={{ color: "light-4" }}
                      style={{ maxHeight: "70vh", overflow: "auto" }}
                    >
                      <Markdown>
                        {formValues.registrationTextFr || "_No content yet_"}
                      </Markdown>
                    </Box>
                  ),
                },
              ]}
            />
            <Button
              label={t("checkinFlow.preCheckin.close")}
              onClick={() => setShowRegistrationPreview(false)}
              primary
            />
          </Box>
        </Layer>
      )}
    </Box>
  );
}
